<script lang="ts" setup>
import { v4 as uuid } from 'uuid'
import type { RankingPageConfig } from '../configs/types'
import { useRanking } from '../composables/useRanking'

interface Props {
  columnId: string
  config: RankingPageConfig
}

const props = defineProps<Props>()

const {
  title,
  books,
  collections,
  fetching,
  ending,
  infiniteRef,
  rankGradient,
} = useRanking(props.columnId, props.config)

function getMedalNumberStyle() {
  if (props.config.theme.medalNumberTop)
    return { top: props.config.theme.medalNumberTop, left: 0, right: 0, bottom: 0 }

  return { inset: 0 }
}
</script>

<template>
  <div class="page h-100dvh overflow-scroll">
    <BasicNavbar class="top-2 color-n-text-2" />

    <div class="title h-60px">
      <img v-if="config.resources.spikeletL" :src="config.resources.spikeletL" width="12px">
      <span class="mx-2">{{ title || '' }}</span>
      <img v-if="config.resources.spikeletR" :src="config.resources.spikeletR" width="12px">
    </div>

    <div ref="infiniteRef" class="container bg-n-bg-card rounded">
      <div v-for="(book, index) in collections" :key="index">
        <router-link
          :to="{ name: 'book', params: { contentId: book.id } }" replace
          class="flex items-center px-3 py-2"
        >
          <div class="medal w-20px shrink-0 relative">
            <img v-if="index === 0" class="medal-img" :src="config.resources.goldMedal">
            <img v-else-if="index === 1" class="medal-img" :src="config.resources.silverMedal">
            <img v-else-if="index === 2" class="medal-img" :src="config.resources.bronze">
            <img v-else class="medal-img" :src="config.resources.medal">

            <div class="absolute flex items-center justify-center z-99 text-n-text-inverse text-12px" :style="getMedalNumberStyle()">
              {{ index + 1 }}
            </div>
          </div>

          <div class="w-66px h-88px border-rd-8px overflow-hidden ml-12px shrink-0">
            <BizBookCover
              :id="uuid()"
              :src="book.contentCoverUrl || ''"
              :extra="{
                module_name: books.showName,
                module_id: books.id,
                module_type: books.columnType,
                author: book.authorInfo.author,
                ...book,
              }"
              class="w-full h-full"
            />
          </div>

          <div class="flex-1 overflow-hidden ml-3">
            <div class="font-500 truncate">
              {{ book.contentName }}
            </div>

            <div class="text-n-text-3 text-12px line-clamp-2 mt-2">
              {{ book.introduce }}
            </div>
            <BizShowTags v-if="book.tagList && book.tagList.length" :tags="book.tagList" class="mt-10px" />
          </div>
        </router-link>
      </div>

      <div v-if="ending" class="py-15px text-13px text-n-text-3 text-center">
        {{ config.texts.noMore }}
      </div>
      <div v-else-if="fetching" class="py-15px flex items-center justify-center space-x-4 text-13px text-n-text-3">
        <div class="animate-spin">
          <i-icon-park-outline-loading />
        </div>
        <p>{{ config.texts.loading }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page {
  background: linear-gradient(180deg, #B4D5FF 0%, rgba(255, 255, 255, 0) 189px);
  background-color: #F6F7F9;
  padding: 0 10px;
}

.title {
  font-family: HelveticaNeue, HelveticaNeue;
  font-weight: bold;
  font-size: 20px;
  text-align: center;
  font-style: normal;
  background: v-bind(rankGradient);
  color: transparent;
  -webkit-background-clip: text;
  display: flex;
  align-items: center;
  justify-content: center;
}

.medal-img {
  display: block;
  width: 100%;
  height: auto;
}

.container {
  overflow-y: scroll;
  height: calc(100dvh - 70px);
  padding-top: 10px;
}
</style>
